/* ============================================================
   PRIMEVAL — MAIN PRODUCT PAGE V4
   Asset: main-product-primeval.js

   Features:
   - Product gallery slider
   - Desktop thumbnails
   - Mobile swipe + dots
   - Image lightbox + zoom
   - 3 enquiry modes: quote / sample / develop
   - 2-step enquiry flow
   - Step 1 buyer validation
   - Step 2 mode-specific validation
   - Back / Continue navigation
   - Progress indicator
   - Unsaved-change protection
   - Firebase Firestore submission
   - Inquiry ID generation
============================================================ */

(() => {
  "use strict";

  const FIREBASE_CONFIG = {
    apiKey: "AIzaSyA-5yM6zWEXzA0R4-cthtbuKNhDesunLFc",
    authDomain: "centrio-4b1b1.firebaseapp.com",
    projectId: "centrio-4b1b1",
    storageBucket: "centrio-4b1b1.firebasestorage.app",
    messagingSenderId: "779575322392",
    appId: "1:779575322392:web:28a92ec5517f4a58e3e06d"
  };

  const SETTINGS = Object.freeze({
    collectionName: "primeval_requests",
    formVersion: "product-page-v4-two-step",
    submitCooldownMs: 5000
  });

  const ENQUIRY_MODES = Object.freeze({
    quote: Object.freeze({
      kicker: "Pricing & customization",
      title: "Request Pricing & Customization",
      subtitle: "Share contact details and product requirements in two easy steps.",
      step2Title: "Pricing & customization",
      step2Subtitle: "Share the commercial information available at this stage.",
      submitLabel: "Request pricing",
      submittingLabel: "Submitting pricing request...",
      successLabel: "pricing request"
    }),

    sample: Object.freeze({
      kicker: "Sample request",
      title: "Request Sample",
      subtitle: "Tell us where to contact you and which product sample you need.",
      step2Title: "Sample requirements",
      step2Subtitle: "Tell us what type of sample you need and where it should be delivered.",
      submitLabel: "Request sample",
      submittingLabel: "Submitting sample request...",
      successLabel: "sample request"
    }),

    develop: Object.freeze({
      kicker: "Product development",
      title: "Develop Similar Product",
      subtitle: "Share a product reference and the changes you need.",
      step2Title: "Development requirements",
      step2Subtitle: "Use this product as a reference and tell us what you would like changed.",
      submitLabel: "Start product development",
      submittingLabel: "Submitting development request...",
      successLabel: "product development request"
    })
  });

  document.addEventListener("DOMContentLoaded", initialisePrimevalProductPage);

  async function initialisePrimevalProductPage() {
    const page = document.getElementById("PrimevalProductPage");

    if (!page) {
      return;
    }

    const productData = readProductData();
    const elements = getElements();

    const state = {
      currentSlide: 0,
      lightboxIndex: 0,
      lightboxScale: 1,
      selectedEnquiryType: "quote",
      currentStep: 1,
      modalOpen: false,
      lightboxOpen: false,
      formDirty: false,
      isSubmitting: false,
      lastSubmitAttempt: 0,
      lastFocusedElement: null,
      firebase: null
    };

    initialiseGallery(elements, state, productData);
    initialiseModal(elements, state);
    initialiseEnquiryFlow(elements, state);
    initialiseForm(elements, state, productData);

    try {
      state.firebase = await initialiseFirebase();
    } catch (error) {
      console.error("Primeval Firebase initialisation failed:", error);
    }
  }

  /* ============================================================
     PRODUCT DATA
  ============================================================ */

  function readProductData() {
    const script = document.getElementById("PrimevalProductData");

    if (!script) {
      return {};
    }

    try {
      return JSON.parse(script.textContent || "{}");
    } catch (error) {
      console.error("Primeval product data could not be parsed:", error);
      return {};
    }
  }

  /* ============================================================
     DOM REFERENCES
  ============================================================ */

  function getElements() {
    const modal = document.getElementById("PrimevalProductEnquiryModal");
    const form = document.getElementById("PrimevalProductEnquiryForm");
    const lightbox = document.getElementById("PrimevalProductLightbox");

    return {
      page: document.getElementById("PrimevalProductPage"),

      galleryTrack: document.getElementById("PrimevalGalleryTrack"),
      gallerySlides: Array.from(document.querySelectorAll(".primeval-gallery-slide")),
      galleryImages: Array.from(document.querySelectorAll(".primeval-gallery-image")),
      galleryPrev: document.querySelector(".primeval-gallery-prev"),
      galleryNext: document.querySelector(".primeval-gallery-next"),
      galleryDots: Array.from(document.querySelectorAll(".primeval-gallery-dot")),
      galleryThumbs: Array.from(document.querySelectorAll(".primeval-gallery-thumb")),

      modal,
      modeButtons: Array.from(modal?.querySelectorAll("[data-primeval-enquiry-route]") || []),
      modalBody: modal?.querySelector(".primeval-product-modal-body"),
      modalOpenButtons: Array.from(document.querySelectorAll("[data-open-product-enquiry]")),
      modalCloseButton: document.getElementById("PrimevalProductEnquiryClose"),
      modalKicker: document.getElementById("PrimevalProductEnquiryKicker"),
      modalTitle: document.getElementById("PrimevalProductEnquiryTitle"),
      modalSubtitle: document.getElementById("PrimevalProductEnquirySubtitle"),

      progress: document.getElementById("PrimevalEnquiryProgress"),
      progressStep1: document.getElementById("PrimevalProgressStep1"),
      progressStep2: document.getElementById("PrimevalProgressStep2"),
      step1Panel: document.getElementById("PrimevalEnquiryStep1"),
      step2Panel: document.getElementById("PrimevalEnquiryStep2"),
      requirementStepTitle: document.getElementById("PrimevalRequirementStepTitle"),
      requirementStepSubtitle: document.getElementById("PrimevalRequirementStepSubtitle"),

      form,
      enquiryTypeInput: document.getElementById("PrimevalEnquiryType"),
      enquiryPanels: Array.from(document.querySelectorAll("[data-enquiry-panel]")),
      continueButton: document.getElementById("PrimevalProductContinue"),
      backButton: document.getElementById("PrimevalProductBack"),
      submitButton: document.getElementById("PrimevalProductSubmit"),
      footerNote: document.getElementById("PrimevalProductFooterNote"),
      formStatus: document.getElementById("PrimevalProductFormStatus"),

      buyerName: document.getElementById("PrimevalBuyerName"),
      buyerEmail: document.getElementById("PrimevalBuyerEmail"),
      buyerCompany: document.getElementById("PrimevalBuyerCompany"),
      buyerCountry: document.getElementById("PrimevalBuyerCountry"),
      buyerPhone: document.getElementById("PrimevalBuyerPhone"),

      quoteQuantity: document.getElementById("PrimevalRequiredQuantity"),
      quoteTargetPrice: document.getElementById("PrimevalTargetPrice"),
      quoteTimeline: document.getElementById("PrimevalTargetTimeline"),
      quoteCustomizationNeed: document.getElementById("PrimevalCustomizationNeed"),
      quoteRequirement: document.getElementById("PrimevalQuoteRequirement"),

      samplePurpose: document.getElementById("PrimevalSamplePurpose"),
      sampleType: document.getElementById("PrimevalSampleType"),
      sampleCountry: document.getElementById("PrimevalSampleCountry"),
      sampleQuantity: document.getElementById("PrimevalSampleQuantity"),
      sampleRequirement: document.getElementById("PrimevalSampleRequirement"),

      developmentQuantity: document.getElementById("PrimevalDevelopmentQuantity"),
      developmentMarket: document.getElementById("PrimevalDevelopmentMarket"),
      developmentTargetPrice: document.getElementById("PrimevalDevelopmentTargetPrice"),
      developmentRequirement: document.getElementById("PrimevalDevelopmentRequirement"),

      lightbox,
      lightboxImage: document.getElementById("PrimevalProductLightboxImage"),
      lightboxClose: document.querySelector(".primeval-lightbox-close"),
      lightboxPrev: document.querySelector(".primeval-lightbox-prev"),
      lightboxNext: document.querySelector(".primeval-lightbox-next")
    };
  }

  /* ============================================================
     FIREBASE
  ============================================================ */

  async function initialiseFirebase() {
    const [firebaseAppModule, firestoreModule] = await Promise.all([
      import("https://www.gstatic.com/firebasejs/12.8.0/firebase-app.js"),
      import("https://www.gstatic.com/firebasejs/12.8.0/firebase-firestore.js")
    ]);

    const app = firebaseAppModule.getApps().length
      ? firebaseAppModule.getApp()
      : firebaseAppModule.initializeApp(FIREBASE_CONFIG);

    return {
      db: firestoreModule.getFirestore(app),
      collection: firestoreModule.collection,
      doc: firestoreModule.doc,
      setDoc: firestoreModule.setDoc,
      serverTimestamp: firestoreModule.serverTimestamp
    };
  }

  /* ============================================================
     GALLERY
  ============================================================ */

  function initialiseGallery(elements, state, productData) {
    const {
      galleryTrack,
      gallerySlides,
      galleryImages,
      galleryPrev,
      galleryNext,
      galleryDots,
      galleryThumbs
    } = elements;

    if (!galleryTrack || !gallerySlides.length) {
      return;
    }

    let touchStartX = 0;
    let touchOffsetX = 0;
    let touching = false;

    function updateGallery(options = {}) {
      const { animate = true } = options;
      const viewport = galleryTrack.parentElement;

      if (!viewport) {
        return;
      }

      const width = viewport.clientWidth;

      galleryTrack.style.transition = animate
        ? "transform 0.42s cubic-bezier(.22,.61,.36,1)"
        : "none";

      galleryTrack.style.transform =
        `translate3d(${-state.currentSlide * width}px, 0, 0)`;

      galleryDots.forEach((dot, index) => {
        const active = index === state.currentSlide;
        dot.classList.toggle("is-active", active);
        dot.setAttribute("aria-current", active ? "true" : "false");
      });

      galleryThumbs.forEach((thumb, index) => {
        const active = index === state.currentSlide;
        thumb.classList.toggle("is-active", active);
        thumb.setAttribute("aria-current", active ? "true" : "false");
      });
    }

    function setSlide(index, options = {}) {
      const total = gallerySlides.length;

      if (!total) {
        return;
      }

      state.currentSlide = (index + total) % total;
      updateGallery(options);
    }

    galleryPrev?.addEventListener("click", () => {
      setSlide(state.currentSlide - 1);
    });

    galleryNext?.addEventListener("click", () => {
      setSlide(state.currentSlide + 1);
    });

    galleryDots.forEach((dot) => {
      dot.addEventListener("click", () => {
        setSlide(Number(dot.dataset.slideTarget || 0));
      });
    });

    galleryThumbs.forEach((thumb) => {
      thumb.addEventListener("click", () => {
        setSlide(Number(thumb.dataset.thumbnailTarget || 0));
      });
    });

    galleryTrack.addEventListener(
      "touchstart",
      (event) => {
        if (event.touches.length !== 1) {
          return;
        }

        touchStartX = event.touches[0].clientX;
        touchOffsetX = 0;
        touching = true;
        galleryTrack.style.transition = "none";
      },
      { passive: true }
    );

    galleryTrack.addEventListener(
      "touchmove",
      (event) => {
        if (!touching || event.touches.length !== 1) {
          return;
        }

        touchOffsetX = event.touches[0].clientX - touchStartX;
        const width = galleryTrack.parentElement.clientWidth;

        galleryTrack.style.transform =
          `translate3d(${(-state.currentSlide * width) + touchOffsetX}px, 0, 0)`;
      },
      { passive: true }
    );

    galleryTrack.addEventListener("touchend", () => {
      if (!touching) {
        return;
      }

      touching = false;

      if (touchOffsetX < -55) {
        state.currentSlide += 1;
      }

      if (touchOffsetX > 55) {
        state.currentSlide -= 1;
      }

      state.currentSlide = Math.max(
        0,
        Math.min(state.currentSlide, gallerySlides.length - 1)
      );

      updateGallery();
    });

    window.addEventListener("resize", () => {
      updateGallery({ animate: false });
    });

    galleryImages.forEach((image, index) => {
      image.addEventListener("click", () => {
        openLightbox(elements, state, index, productData);
      });
    });

    initialiseLightbox(elements, state, productData);
    updateGallery({ animate: false });
  }

  /* ============================================================
     LIGHTBOX
  ============================================================ */

  function initialiseLightbox(elements, state, productData) {
    const {
      lightbox,
      lightboxImage,
      lightboxClose,
      lightboxPrev,
      lightboxNext,
      galleryImages
    } = elements;

    if (!lightbox || !lightboxImage || !galleryImages.length) {
      return;
    }

    lightboxClose?.addEventListener("click", () => {
      closeLightbox(elements, state);
    });

    lightboxPrev?.addEventListener("click", () => {
      state.lightboxIndex =
        (state.lightboxIndex - 1 + galleryImages.length) % galleryImages.length;

      updateLightboxImage(elements, state, productData);
    });

    lightboxNext?.addEventListener("click", () => {
      state.lightboxIndex =
        (state.lightboxIndex + 1) % galleryImages.length;

      updateLightboxImage(elements, state, productData);
    });

    lightbox.addEventListener("click", (event) => {
      if (event.target === lightbox) {
        closeLightbox(elements, state);
      }
    });

    lightboxImage.addEventListener("click", (event) => {
      event.stopPropagation();

      state.lightboxScale = state.lightboxScale >= 3
        ? 1
        : state.lightboxScale + 0.75;

      lightboxImage.style.transform = `scale(${state.lightboxScale})`;
      lightboxImage.style.cursor = state.lightboxScale >= 3
        ? "zoom-out"
        : "zoom-in";
    });

    document.addEventListener("keydown", (event) => {
      if (!state.lightboxOpen) {
        return;
      }

      if (event.key === "ArrowLeft") {
        lightboxPrev?.click();
      }

      if (event.key === "ArrowRight") {
        lightboxNext?.click();
      }

      if (event.key === "Escape") {
        closeLightbox(elements, state);
      }
    });
  }

  function openLightbox(elements, state, index, productData) {
    const { lightbox } = elements;

    if (!lightbox) {
      return;
    }

    state.lastFocusedElement = document.activeElement;
    state.lightboxIndex = index;
    state.lightboxOpen = true;

    updateLightboxImage(elements, state, productData);

    lightbox.classList.add("is-open");
    lightbox.setAttribute("aria-hidden", "false");

    lockPage();
    elements.lightboxClose?.focus();
  }

  function closeLightbox(elements, state) {
    const { lightbox } = elements;

    if (!lightbox) {
      return;
    }

    state.lightboxOpen = false;

    lightbox.classList.remove("is-open");
    lightbox.setAttribute("aria-hidden", "true");

    unlockPageIfPossible(state);
    restoreFocus(state);
  }

  function updateLightboxImage(elements, state, productData) {
    const image = elements.galleryImages[state.lightboxIndex];

    if (!image || !elements.lightboxImage) {
      return;
    }

    state.lightboxScale = 1;

    elements.lightboxImage.src =
      image.dataset.zoomSrc ||
      image.currentSrc ||
      image.src;

    elements.lightboxImage.alt =
      image.alt ||
      productData.title ||
      "Product image";

    elements.lightboxImage.style.transform = "scale(1)";
    elements.lightboxImage.style.cursor = "zoom-in";
  }

  /* ============================================================
     MODAL
  ============================================================ */

  function initialiseModal(elements, state) {
    const {
      modal,
      modalOpenButtons,
      modalCloseButton,
      form
    } = elements;

    if (!modal || !form) {
      return;
    }

    modalOpenButtons.forEach((button) => {
      button.addEventListener("click", () => {
        openModal(
          elements,
          state,
          button.dataset.enquiryType || "quote"
        );
      });
    });

    // Switch enquiry routes inside the popup without clearing contact information.
    elements.modeButtons.forEach((button) => {
      button.addEventListener("click", () => {
        const kind = button.dataset.primevalEnquiryRoute;
        if (!state.modalOpen || state.isSubmitting ||
            !Object.prototype.hasOwnProperty.call(ENQUIRY_MODES, kind) ||
            kind === state.selectedEnquiryType) return;
        selectEnquiryType(elements, state, kind);
        setStep(elements, state, state.currentStep, { focus: false });
      });
    });

    modalCloseButton?.addEventListener("click", () => {
      requestCloseModal(elements, state);
    });

    modal.addEventListener("click", (event) => {
      if (event.target === modal) {
        requestCloseModal(elements, state);
      }
    });

    document.addEventListener("keydown", (event) => {
      if (!state.modalOpen || state.lightboxOpen) {
        return;
      }

      if (event.key === "Escape") {
        event.preventDefault();
        requestCloseModal(elements, state);
      }

      if (event.key === "Tab") {
        trapFocus(event, modal);
      }
    });

    form.addEventListener("input", () => {
      state.formDirty = true;
    });

    form.addEventListener("change", () => {
      state.formDirty = true;
    });
  }

  function openModal(elements, state, enquiryType = "quote") {
    const { modal, modalBody, form } = elements;

    if (!modal || !form) {
      return;
    }

    state.lastFocusedElement = document.activeElement;
    state.modalOpen = true;
    state.formDirty = false;

    form.reset();
    clearConditionalValidation(elements);
    clearStatus(elements);

    selectEnquiryType(elements, state, enquiryType);
    setStep(elements, state, 1, { focus: false });

    modal.classList.add("is-open");
    modal.setAttribute("aria-hidden", "false");

    if (modalBody) {
      modalBody.scrollTop = 0;
    }

    lockPage();

    window.setTimeout(() => {
      (
        elements.buyerName ||
        elements.modalCloseButton
      )?.focus();
    }, 70);
  }

  function requestCloseModal(elements, state) {
    if (state.isSubmitting) {
      return;
    }

    if (state.formDirty && hasMeaningfulFormData(elements.form)) {
      const confirmed = window.confirm(
        "Close this enquiry? The information entered in this form will be cleared."
      );

      if (!confirmed) {
        return;
      }
    }

    closeModal(elements, state);
  }

  function closeModal(elements, state) {
    const { modal, form } = elements;

    if (!modal) {
      return;
    }

    state.modalOpen = false;
    state.formDirty = false;

    modal.classList.remove("is-open");
    modal.setAttribute("aria-hidden", "true");
    modal.setAttribute("aria-busy", "false");

    if (form && !state.isSubmitting) {
      form.reset();
      form.setAttribute("aria-busy", "false");
    }

    selectEnquiryType(elements, state, "quote");
    setStep(elements, state, 1, { focus: false });
    clearConditionalValidation(elements);
    clearStatus(elements);

    unlockPageIfPossible(state);
    restoreFocus(state);
  }

  /* ============================================================
     TWO-STEP ENQUIRY FLOW
  ============================================================ */

  function initialiseEnquiryFlow(elements, state) {
    elements.continueButton?.addEventListener("click", () => {
      if (!validateStep1(elements)) {
        return;
      }

      clearStatus(elements);
      setStep(elements, state, 2, { focus: true });
    });

    elements.backButton?.addEventListener("click", () => {
      clearStatus(elements);
      setStep(elements, state, 1, { focus: true });
    });

    selectEnquiryType(elements, state, "quote");
    setStep(elements, state, 1, { focus: false });
  }

  function primevalModeDetails(elements, type) {
    const base = ENQUIRY_MODES[type] || ENQUIRY_MODES.quote;
    if (elements.modal?.dataset.primevalWeeklySpecial !== "true") return base;
    const active = elements.modal.dataset.primevalOfferOpen === "true";
    if (type === "quote") return active ? {
      ...base, kicker:"Weekly Specials · Limited-time offer",
      title:"Submit Weekly Special Enquiry",
      subtitle:"Review your offer and request confirmation. This is not a payment or confirmed order.",
      step2Title:"Your Weekly Special enquiry",
      step2Subtitle:"Confirm quantity and specifications. Primeval will verify the final commercial terms.",
      submitLabel:"Submit offer enquiry", submittingLabel:"Sending offer enquiry..."
    } : {
      ...base, kicker:"Weekly Specials · Current terms",
      title:"Request Updated Quotation",
      subtitle:"This promotional offer is not currently available. Request fresh terms.",
      step2Title:"Request new pricing",
      step2Subtitle:"Share the quantity and specifications required for a new quote.",
      submitLabel:"Request updated quote", submittingLabel:"Sending quote request..."
    };
    if (type === "sample") return {...base,kicker:"Weekly Specials · Samples",title:"Ask About a Sample",subtitle:"Check sample availability and courier cost for this design."};
    return {...base,kicker:"Weekly Specials · Custom design",title:"Customise This Design",subtitle:"Describe your desired changes. Promotional prices may not apply."};
  }

  function selectEnquiryType(elements, state, enquiryType) {
    const safeType = Object.prototype.hasOwnProperty.call(
      ENQUIRY_MODES,
      enquiryType
    )
      ? enquiryType
      : "quote";

    const mode = primevalModeDetails(elements, safeType);

    state.selectedEnquiryType = safeType;

    elements.modeButtons.forEach((button) => {
      button.setAttribute("aria-pressed", String(button.dataset.primevalEnquiryRoute === safeType));
    });

    if (elements.modal) {
      elements.modal.dataset.enquiryMode = safeType;
    }

    if (elements.enquiryTypeInput) {
      elements.enquiryTypeInput.value = safeType;
    }

    if (elements.modalKicker) {
      elements.modalKicker.textContent = mode.kicker;
    }

    if (elements.modalTitle) {
      elements.modalTitle.textContent = mode.title;
    }

    if (elements.modalSubtitle) {
      elements.modalSubtitle.textContent = mode.subtitle;
    }

    if (elements.requirementStepTitle) {
      elements.requirementStepTitle.textContent = mode.step2Title;
    }

    if (elements.requirementStepSubtitle) {
      elements.requirementStepSubtitle.textContent = mode.step2Subtitle;
    }

    elements.enquiryPanels.forEach((panel) => {
      const active = panel.dataset.enquiryPanel === safeType;

      panel.hidden = !active;

      panel
        .querySelectorAll("input, select, textarea")
        .forEach((field) => {
          field.disabled = !active;
        });
    });

    configureRequirements(elements, safeType);
    updateSubmitButton(elements, state, false);
    clearConditionalValidation(elements);
  }

  function setStep(elements, state, step, options = {}) {
    const { focus = true } = options;
    const safeStep = step === 2 ? 2 : 1;

    state.currentStep = safeStep;

    if (elements.modal) {
      elements.modal.dataset.enquiryStep = String(safeStep);
    }

    if (elements.progress) {
      elements.progress.dataset.currentStep = String(safeStep);
    }

    if (elements.step1Panel) {
      elements.step1Panel.hidden = safeStep !== 1;
    }

    if (elements.step2Panel) {
      elements.step2Panel.hidden = safeStep !== 2;
    }

    elements.progressStep1?.classList.toggle("is-active", safeStep === 1);
    elements.progressStep1?.classList.toggle("is-complete", safeStep === 2);
    elements.progressStep2?.classList.toggle("is-active", safeStep === 2);

    if (elements.backButton) {
      elements.backButton.hidden = safeStep !== 2;
    }

    if (elements.continueButton) {
      elements.continueButton.hidden = safeStep !== 1;
    }

    if (elements.submitButton) {
      elements.submitButton.hidden = safeStep !== 2;
    }

    if (elements.footerNote) {
      elements.footerNote.textContent = safeStep === 1
        ? "Step 1 of 2 — contact details."
        : getStep2FooterNote(state.selectedEnquiryType);
    }

    if (elements.modalBody) {
      elements.modalBody.scrollTop = 0;
    }

    clearConditionalValidation(elements);

    if (!focus) {
      return;
    }

    window.setTimeout(() => {
      if (safeStep === 1) {
        elements.buyerName?.focus();
        return;
      }

      getFirstStep2Field(elements, state.selectedEnquiryType)?.focus();
    }, 50);
  }

  function getStep2FooterNote(enquiryType) {
    if (enquiryType === "sample") {
      return "Step 2 of 2 — tell us what sample you need.";
    }

    if (enquiryType === "develop") {
      return "Step 2 of 2 — tell us how you want the product developed.";
    }

    return "Step 2 of 2 — share quantity and buying requirements.";
  }

  function getFirstStep2Field(elements, enquiryType) {
    if (enquiryType === "sample") {
      return elements.samplePurpose;
    }

    if (enquiryType === "develop") {
      return elements.developmentQuantity || elements.developmentRequirement;
    }

    return elements.quoteQuantity;
  }

  function configureRequirements(elements, enquiryType) {
    if (elements.quoteQuantity) {
      elements.quoteQuantity.required = enquiryType === "quote";
    }

    if (elements.samplePurpose) {
      elements.samplePurpose.required = enquiryType === "sample";
    }

    if (elements.sampleType) {
      elements.sampleType.required = enquiryType === "sample";
    }

    if (elements.sampleCountry) {
      elements.sampleCountry.required = enquiryType === "sample";
    }

    if (elements.developmentRequirement) {
      elements.developmentRequirement.required = enquiryType === "develop";
    }
  }

  /* ============================================================
     VALIDATION
  ============================================================ */

  function validateStep1(elements) {
    const fields = [
      elements.buyerName,
      elements.buyerEmail,
      elements.buyerCompany,
      elements.buyerCountry
    ].filter(Boolean);

    if (!validateFieldCollection(fields, elements)) {
      return false;
    }

    if (
      elements.buyerEmail &&
      elements.buyerEmail.value &&
      !isValidEmail(elements.buyerEmail.value)
    ) {
      elements.buyerEmail.setAttribute("aria-invalid", "true");

      showStatus(
        elements,
        "Please enter a valid work email address.",
        "error"
      );

      elements.buyerEmail.focus();
      return false;
    }

    clearStatus(elements);
    return true;
  }

  function validateStep2(elements, state) {
    configureRequirements(elements, state.selectedEnquiryType);

    let fields = [];

    if (state.selectedEnquiryType === "sample") {
      fields = [
        elements.samplePurpose,
        elements.sampleType,
        elements.sampleCountry,
        elements.sampleQuantity,
        elements.sampleRequirement
      ];
    } else if (state.selectedEnquiryType === "develop") {
      fields = [
        elements.developmentQuantity,
        elements.developmentMarket,
        elements.developmentTargetPrice,
        elements.developmentRequirement
      ];
    } else {
      fields = [
        elements.quoteQuantity,
        elements.quoteTargetPrice,
        elements.quoteTimeline,
        elements.quoteCustomizationNeed,
        elements.quoteRequirement
      ];
    }

    return validateFieldCollection(fields.filter(Boolean), elements);
  }

  function validateFieldCollection(fields, elements) {
    let firstInvalid = null;

    fields.forEach((field) => {
      field.removeAttribute("aria-invalid");

      if (!field.checkValidity() && !firstInvalid) {
        firstInvalid = field;
      }
    });

    if (!firstInvalid) {
      return true;
    }

    firstInvalid.setAttribute("aria-invalid", "true");

    showStatus(
      elements,
      getValidationMessage(firstInvalid),
      "error"
    );

    firstInvalid.scrollIntoView({
      behavior: prefersReducedMotion() ? "auto" : "smooth",
      block: "center"
    });

    window.setTimeout(() => {
      firstInvalid.focus({ preventScroll: true });
    }, 180);

    return false;
  }

  function getValidationMessage(field) {
    const messages = {
      name: "Please enter your full name.",
      email: "Please enter your work email.",
      company: "Please enter your company or brand.",
      country: "Please enter your country.",
      quantity: "Please enter the required quantity.",
      samplePurpose: "Please select the sample purpose.",
      sampleType: "Please select the sample type.",
      sampleCountry: "Please enter the sample delivery country.",
      requirements: "Please describe what you would like to change for this product."
    };

    return messages[field.name] || "Please complete the required information.";
  }

  function clearConditionalValidation(elements) {
    elements.form
      ?.querySelectorAll("[aria-invalid='true']")
      .forEach((field) => {
        field.removeAttribute("aria-invalid");
      });
  }

  /* ============================================================
     FORM SUBMISSION
  ============================================================ */

  function initialiseForm(elements, state, productData) {
    const { form } = elements;

    if (!form) {
      return;
    }

    form.addEventListener("submit", async (event) => {
      event.preventDefault();

      if (state.isSubmitting) {
        return;
      }

      if (state.currentStep !== 2) {
        if (validateStep1(elements)) {
          setStep(elements, state, 2, { focus: true });
        }
        return;
      }

      if (!validateStep1(elements)) {
        setStep(elements, state, 1, { focus: false });
        return;
      }

      if (!validateStep2(elements, state)) {
        return;
      }

      const now = Date.now();

      if (now - state.lastSubmitAttempt < SETTINGS.submitCooldownMs) {
        showStatus(
          elements,
          "Please wait a few seconds before submitting again.",
          "error"
        );
        return;
      }

      if (!state.firebase) {
        try {
          state.firebase = await initialiseFirebase();
        } catch (error) {
          console.error("Primeval Firebase retry failed:", error);

          showStatus(
            elements,
            "The enquiry service is temporarily unavailable. Please email sourcing@primeval.in.",
            "error"
          );
          return;
        }
      }

      try {
        const firebase = state.firebase;

        const requestReference = firebase.doc(
          firebase.collection(
            firebase.db,
            SETTINGS.collectionName
          )
        );

        const inquiryId = createInquiryId(requestReference.id);

        /* Build payload before any submission-state UI changes. */
        const payload = buildPayload(
          form,
          state,
          productData,
          inquiryId,
          requestReference.id,
          firebase.serverTimestamp
        );

        state.lastSubmitAttempt = now;
        state.isSubmitting = true;

        setSubmittingState(elements, state, true);

        showStatus(
          elements,
          primevalModeDetails(elements, state.selectedEnquiryType).submittingLabel,
          "loading"
        );

        await firebase.setDoc(requestReference, payload);

        showStatus(
          elements,
          getSuccessMessage(
            state.selectedEnquiryType,
            inquiryId
          ),
          "success"
        );

        state.formDirty = false;

        window.setTimeout(() => {
          closeModalAfterSuccess(elements, state);
        }, 2400);
      } catch (error) {
        console.error("Primeval product enquiry failed:", error);

        showStatus(
          elements,
          getFriendlyError(error),
          "error"
        );
      } finally {
        state.isSubmitting = false;
        setSubmittingState(elements, state, false);
      }
    });
  }

  function updateSubmitButton(elements, state, loading) {
    if (!elements.submitButton) {
      return;
    }

    const mode = primevalModeDetails(elements, state.selectedEnquiryType);

    elements.submitButton.textContent = loading
      ? mode.submittingLabel
      : mode.submitLabel;
  }

  function getSubmittingMessage(enquiryType) {
    const mode = ENQUIRY_MODES[enquiryType] || ENQUIRY_MODES.quote;
    return mode.submittingLabel;
  }

  function getSuccessMessage(enquiryType, inquiryId) {
    const mode = ENQUIRY_MODES[enquiryType] || ENQUIRY_MODES.quote;

    return `Thank you. Your ${mode.successLabel} reference is ${inquiryId}. Primeval will normally respond within one business day.`;
  }

  /* ============================================================
     PAYLOAD
  ============================================================ */

  function buildPayload(
    form,
    state,
    productData,
    inquiryId,
    documentId,
    serverTimestamp
  ) {
    const data = new FormData(form);
    const params = new URLSearchParams(window.location.search);

    const enquiryType =
      cleanText(data.get("enquiryType"), 40) ||
      state.selectedEnquiryType;

    return {
      inquiryId,
      documentId,

      requestSource: "product_page",
      requestType: enquiryType,
      formVersion: SETTINGS.formVersion,
      formStepCount: 2,

      status: "new",
      stage: "initial_review",
      priority: calculatePriority(data, enquiryType),

      product: {
        id: productData.id || "",
        title: productData.title || "",
        handle: productData.handle || "",
        reference: productData.reference || "",
        url: productData.url || "",
        featuredImage: productData.featuredImage || "",
        material: productData.material || "",
        size: productData.size || "",
        moq: productData.moq || "",
        price: productData.price || "",
        leadTime: productData.leadTime || "",
        supplierLocation: productData.supplierLocation || ""
      },

      buyer: {
        name: cleanText(data.get("name"), 150),
        email: String(data.get("email") || "")
          .trim()
          .toLowerCase(),
        company: cleanText(data.get("company"), 180),
        country: cleanText(data.get("country"), 100),
        phone: cleanText(data.get("phone"), 50)
      },

      commercial: {
        quantity: cleanText(
          data.get("quantity") ||
          data.get("developmentQuantity"),
          150
        ),

        timeline: cleanText(
          data.get("timeline"),
          100
        ),

        targetPrice: cleanText(
          data.get("targetPrice"),
          150
        ),

        customizationNeed: cleanText(
          data.get("customizationNeed"),
          150
        ),

        samplePurpose: cleanText(
          data.get("samplePurpose"),
          120
        ),

        sampleType: cleanText(
          data.get("sampleType"),
          120
        ),

        sampleCountry: cleanText(
          data.get("sampleCountry"),
          100
        ),

        sampleQuantity: cleanText(
          data.get("sampleQuantity"),
          50
        ),

        developmentMarket: cleanText(
          data.get("developmentMarket"),
          120
        )
      },

      requirements: cleanMultiline(
        data.get("requirements"),
        3000
      ),

      tracking: {
        pageUrl: window.location.href,
        pagePath: window.location.pathname,
        pageTitle: document.title || "",
        referrer: document.referrer || "",

        utmSource: cleanText(
          params.get("utm_source"),
          150
        ),

        utmMedium: cleanText(
          params.get("utm_medium"),
          150
        ),

        utmCampaign: cleanText(
          params.get("utm_campaign"),
          200
        ),

        timezone:
          Intl.DateTimeFormat()
            .resolvedOptions()
            .timeZone || "",

        language: navigator.language || "",
        userAgent: navigator.userAgent || ""
      },

      sourceLabel:
        productData.source ||
        "Primeval Product Page",

      ...(productData.weeklySpecial ? {
        weeklySpecial: {
          status: cleanText(productData.weeklySpecial.status, 40),
          priceCurrency: "USD",
          unitPrice: productData.weeklySpecial.unitPrice ?? null,
          minimumQuantity: productData.weeklySpecial.minimumQuantity ?? null,
          offerEndDate: productData.weeklySpecial.offerEndDate ?? null,
          targetDispatchDate: productData.weeklySpecial.targetDispatchDate ?? null,
          requiresConfirmation: true
        }
      } : {}),

      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };
  }

  function calculatePriority(data, enquiryType) {
    const timeline = String(data.get("timeline") || "");

    const quantity = String(
      data.get("quantity") ||
      data.get("developmentQuantity") ||
      ""
    );

    if (
      enquiryType === "quote" &&
      (
        timeline === "Immediately" ||
        timeline === "Within 1 Month"
      )
    ) {
      return "high";
    }

    if (
      enquiryType === "sample" &&
      String(data.get("samplePurpose") || "") === "Bulk Order Planning"
    ) {
      return "high";
    }

    if (
      enquiryType === "develop" &&
      quantity.trim()
    ) {
      return "normal";
    }

    return "normal";
  }

  /* ============================================================
     SUBMISSION UI
  ============================================================ */

  function showStatus(elements, message, type = "") {
    const { formStatus } = elements;

    if (!formStatus) {
      return;
    }

    formStatus.textContent = message;
    formStatus.className = "primeval-product-form-status";

    if (type) {
      formStatus.classList.add(`is-${type}`);
    }

    if (type === "success") {
      formStatus.style.color = "#067647";
      formStatus.style.fontWeight = "700";
    } else if (type === "error") {
      formStatus.style.color = "#b42318";
      formStatus.style.fontWeight = "700";
    } else if (type === "loading") {
      formStatus.style.color = "#1849a9";
      formStatus.style.fontWeight = "700";
    } else {
      formStatus.style.color = "";
      formStatus.style.fontWeight = "";
    }
  }

  function clearStatus(elements) {
    const { formStatus } = elements;

    if (!formStatus) {
      return;
    }

    formStatus.textContent = "";
    formStatus.className = "primeval-product-form-status";
    formStatus.style.color = "";
    formStatus.style.fontWeight = "";
  }

  function setSubmittingState(elements, state, loading) {
    if (elements.submitButton) {
      elements.submitButton.disabled = loading;
    }

    if (elements.continueButton) {
      elements.continueButton.disabled = loading;
    }

    if (elements.backButton) {
      elements.backButton.disabled = loading;
    }

    if (elements.modalCloseButton) {
      elements.modalCloseButton.disabled = loading;
    }

    if (elements.modal) {
      elements.modal.setAttribute("aria-busy", loading ? "true" : "false");
    }

    if (elements.form) {
      elements.form.setAttribute("aria-busy", loading ? "true" : "false");
    }

    /* Do not disable form fields: disabled controls are excluded from FormData. */
    updateSubmitButton(elements, state, loading);
  }

  function closeModalAfterSuccess(elements, state) {
    const { modal, form } = elements;

    if (!modal) {
      return;
    }

    state.modalOpen = false;
    state.formDirty = false;

    modal.classList.remove("is-open");
    modal.setAttribute("aria-hidden", "true");
    modal.setAttribute("aria-busy", "false");

    form?.reset();
    form?.setAttribute("aria-busy", "false");

    selectEnquiryType(elements, state, "quote");
    setStep(elements, state, 1, { focus: false });
    clearConditionalValidation(elements);
    clearStatus(elements);

    unlockPageIfPossible(state);
    restoreFocus(state);
  }

  /* ============================================================
     ACCESSIBILITY
  ============================================================ */

  function trapFocus(event, container) {
    const focusable = Array.from(
      container.querySelectorAll(
        [
          "a[href]",
          "button:not([disabled]):not([hidden])",
          "input:not([disabled]):not([type='hidden'])",
          "select:not([disabled])",
          "textarea:not([disabled])",
          "[tabindex]:not([tabindex='-1'])"
        ].join(",")
      )
    ).filter((element) => element.offsetParent !== null);

    if (!focusable.length) {
      return;
    }

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (
      event.shiftKey &&
      document.activeElement === first
    ) {
      event.preventDefault();
      last.focus();
      return;
    }

    if (
      !event.shiftKey &&
      document.activeElement === last
    ) {
      event.preventDefault();
      first.focus();
    }
  }

  function lockPage() {
    document.documentElement.classList.add("primeval-modal-open");
    document.body.classList.add("primeval-modal-open");
  }

  function unlockPageIfPossible(state) {
    if (state.modalOpen || state.lightboxOpen) {
      return;
    }

    document.documentElement.classList.remove("primeval-modal-open");
    document.body.classList.remove("primeval-modal-open");
  }

  function restoreFocus(state) {
    const target = state.lastFocusedElement;

    if (
      target &&
      typeof target.focus === "function"
    ) {
      window.setTimeout(() => {
        target.focus();
      }, 50);
    }
  }

  /* ============================================================
     HELPERS
  ============================================================ */

  function createInquiryId(documentId) {
    const now = new Date();

    const date =
      `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, "0")}${String(now.getDate()).padStart(2, "0")}`;

    const fragment = String(documentId)
      .replace(/[^a-zA-Z0-9]/g, "")
      .slice(0, 7)
      .toUpperCase();

    return `PV-${date}-${fragment}`;
  }

  function hasMeaningfulFormData(form) {
    if (!form) {
      return false;
    }

    const data = new FormData(form);

    for (const [key, value] of data.entries()) {
      if (key === "enquiryType") {
        continue;
      }

      if (String(value || "").trim()) {
        return true;
      }
    }

    return false;
  }

  function cleanText(value, maxLength) {
    return String(value || "")
      .replace(/\u0000/g, "")
      .replace(/\s+/g, " ")
      .trim()
      .slice(0, maxLength);
  }

  function cleanMultiline(value, maxLength) {
    return String(value || "")
      .replace(/\u0000/g, "")
      .replace(/\r\n/g, "\n")
      .replace(/[ \t]+\n/g, "\n")
      .replace(/\n{4,}/g, "\n\n\n")
      .trim()
      .slice(0, maxLength);
  }

  function isValidEmail(value) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i.test(
      String(value || "").trim()
    );
  }

  function getFriendlyError(error) {
    const code = String(error?.code || "");

    const messages = {
      "permission-denied":
        "The enquiry service is not accepting submissions right now. Please email sourcing@primeval.in.",

      "firestore/permission-denied":
        "The enquiry service is not accepting submissions right now. Please email sourcing@primeval.in.",

      "unavailable":
        "The enquiry service is temporarily unavailable. Please try again shortly.",

      "firestore/unavailable":
        "The enquiry service is temporarily unavailable. Please try again shortly.",

      "deadline-exceeded":
        "The enquiry timed out. Please check your connection and try again.",

      "firestore/deadline-exceeded":
        "The enquiry timed out. Please check your connection and try again."
    };

    return (
      messages[code] ||
      "Your enquiry could not be submitted. Please try again or email sourcing@primeval.in."
    );
  }

  function prefersReducedMotion() {
    return Boolean(
      window.matchMedia?.("(prefers-reduced-motion: reduce)").matches
    );
  }
})();
